import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, X } from "lucide-react";

/**
 * Persistent banner shown when an agency user is operating as a client.
 * Renders at the top of Layout; shows "Exit to my workspace" action.
 */
export default function OperateAsBanner() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let active = true;
    const load = () => {
      api.get("/agency/operate/status")
        .then((r) => { if (active) setStatus(r.data); })
        .catch(() => { if (active) setStatus(null); });
    };
    load();
    const interval = setInterval(load, 60000);
    const handler = () => load();
    window.addEventListener("agency-operate-change", handler);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener("agency-operate-change", handler);
    };
  }, []);

  if (!status?.active) return null;

  const exit = async () => {
    try {
      await api.post("/agency/operate/exit");
      toast.success("Exited client workspace");
      window.dispatchEvent(new Event("agency-operate-change"));
      // Full reload to clear tenant-scoped caches
      window.location.href = "/agency-dashboard";
    } catch (e) {
      toast.error("Failed to exit — refresh the page");
    }
  };

  return (
    <div
      className="w-full bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 border-b border-amber-500/40 px-4 py-2 flex items-center justify-between gap-3"
      data-testid="operate-as-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 animate-pulse" />
        <span className="text-xs sm:text-sm font-semibold text-amber-300 truncate">
          Operating as{" "}
          <span className="text-amber-100" data-testid="operating-as-name">
            {status.operating_name}
          </span>{" "}
          on behalf of {status.agency_name}. All actions are audit-logged.
        </span>
      </div>
      <button
        onClick={exit}
        className="text-[11px] font-bold px-3 py-1 rounded-md bg-amber-500 hover:bg-amber-400 text-black transition-all shrink-0 flex items-center gap-1"
        data-testid="exit-operate-as-btn"
      >
        <X className="w-3 h-3" /> Exit
      </button>
    </div>
  );
}
